import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Sparkles, Star, Heart, Eye, AtSign, Award } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { tgOpenInvoice } from "../lib/telegram";
import { t } from "../lib/i18n";

const BENEFIT_ICONS = [Star, Heart, Eye, AtSign, Award];
const BENEFIT_KEYS = [
  "premium_benefit_superlikes",
  "premium_benefit_unlimited_likes",
  "premium_benefit_likes_received",
  "premium_benefit_username",
  "premium_benefit_badge",
];

const POLL_INTERVAL_MS = 1500;
const POLL_MAX_ATTEMPTS = 8;

export default function Premium() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const lang = user?.language || "ru";
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyingDays, setBuyingDays] = useState(null);
  const [confirmingDays, setConfirmingDays] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/premium/pricing")
      .then(({ data }) => setTiers(data.tiers || []))
      .catch(() => toast.error(t(lang, "error_generic")))
      .finally(() => setLoading(false));
  }, [lang]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const pollForActivation = (days, prevUntil) => {
    setConfirmingDays(days);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      const fresh = await refresh();
      if (fresh?.is_premium && fresh.premium_until !== prevUntil) {
        clearInterval(pollRef.current);
        setConfirmingDays(null);
        toast.success(t(lang, "premium_success"));
      } else if (attempts >= POLL_MAX_ATTEMPTS) {
        clearInterval(pollRef.current);
        setConfirmingDays(null);
        toast.message(t(lang, "premium_confirming"));
      }
    }, POLL_INTERVAL_MS);
  };

  const buy = async (days) => {
    if (buyingDays || confirmingDays) return;
    setBuyingDays(days);
    try {
      const { data } = await api.post("/premium/invoice", { days });
      let status;
      try {
        status = await tgOpenInvoice(data.invoice_link);
      } catch {
        toast.error(t(lang, "premium_unavailable_outside_telegram"));
        return;
      }
      if (status === "paid") {
        pollForActivation(days, user?.premium_until || null);
      } else if (status === "cancelled") {
        toast.message(t(lang, "premium_cancelled"));
      } else if (status === "failed") {
        toast.error(t(lang, "premium_failed"));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t(lang, "error_generic"));
    } finally {
      setBuyingDays(null);
    }
  };

  const premiumUntilLabel = user?.premium_until
    ? new Date(user.premium_until).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <div className="kupidon-shell safe-top safe-bottom flex flex-col relative overflow-y-auto no-scrollbar pb-10" data-testid="premium-page">
      <div className="absolute top-0 left-0 right-0 h-56 bg-gradient-to-b from-[color:var(--peach)]/12 to-transparent pointer-events-none" />

      <div className="px-5 pt-4 relative z-10 flex items-center gap-3">
        <button
          onClick={() => nav(-1)}
          className="w-9 h-9 rounded-2xl bg-white/90 border border-[color:var(--border)] shadow-sm flex items-center justify-center hover:bg-white transition-all active:scale-90"
          aria-label="Назад"
        >
          <ChevronLeft className="w-5 h-5 text-[color:var(--ink)]" />
        </button>
        <div>
          <div className="font-[Onest] font-extrabold text-2xl tracking-tight flex items-center gap-1.5">
            <Sparkles className="w-5 h-5 text-[color:var(--peach-deep)]" />
            {t(lang, "premium_title")}
          </div>
          <div className="text-xs text-[color:var(--ink-soft)]">{t(lang, "premium_subtitle")}</div>
        </div>
      </div>

      <div className="px-5 mt-4 relative z-10">
        <div className={`rounded-2xl p-4 border ${user?.is_premium ? "bg-[color:var(--peach)]/10 border-[color:var(--peach)]/30" : "bg-white/90 border-[color:var(--border)]"}`}>
          {user?.is_premium ? (
            <div className="font-semibold text-sm flex items-center gap-1.5 text-[color:var(--peach-deep)]">
              <Sparkles className="w-4 h-4 text-[color:var(--stars-gold)]" />
              {t(lang, "premium_current_status_active", { date: premiumUntilLabel })}
            </div>
          ) : (
            <div className="text-sm text-[color:var(--ink-soft)]">{t(lang, "premium_current_status_free")}</div>
          )}
        </div>
      </div>

      <div className="px-5 mt-4 relative z-10 space-y-2">
        {BENEFIT_KEYS.map((key, i) => {
          const Icon = BENEFIT_ICONS[i];
          return (
            <div key={key} className="flex items-center gap-2.5 text-sm">
              <div className="w-7 h-7 rounded-xl bg-[color:var(--peach)]/12 text-[color:var(--peach-deep)] flex items-center justify-center flex-shrink-0">
                <Icon className="w-3.5 h-3.5" />
              </div>
              {t(lang, key)}
            </div>
          );
        })}
      </div>

      <div className="px-5 mt-5 relative z-10 grid grid-cols-2 gap-3">
        {loading && (
          <div className="col-span-2 flex justify-center py-10">
            <div className="w-8 h-8 rounded-full border-2 border-[color:var(--peach)] border-t-transparent animate-spin" />
          </div>
        )}
        {!loading && tiers.map((tier) => {
          const isPopular = tier.days === 30;
          return (
            <motion.div
              key={tier.days}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`relative rounded-2xl bg-white/90 border p-4 flex flex-col items-center gap-2 ${
                isPopular ? "border-[color:var(--peach)] shadow-md shadow-[color:var(--peach)]/15" : "border-[color:var(--border)]"
              }`}
            >
              {isPopular && (
                <div className="absolute -top-2.5 px-2 py-0.5 rounded-full bg-[color:var(--peach-deep)] text-white text-[9px] font-bold uppercase tracking-wide">
                  Популярно
                </div>
              )}
              <div className="font-[Onest] font-bold text-sm mt-1">{t(lang, "premium_tier_days", { days: tier.days })}</div>
              <div className="font-[Onest] font-extrabold text-xl flex items-center gap-1">
                ⭐ {tier.stars}
              </div>
              <button
                onClick={() => buy(tier.days)}
                disabled={!!buyingDays || !!confirmingDays}
                className="w-full mt-1 py-2 rounded-xl bg-[color:var(--ink)] text-white text-xs font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {buyingDays === tier.days || confirmingDays === tier.days ? (
                  <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                ) : (
                  t(lang, "premium_buy_button")
                )}
              </button>
            </motion.div>
          );
        })}
      </div>

      {confirmingDays !== null && (
        <div className="px-5 mt-4 relative z-10 text-center text-xs text-[color:var(--ink-soft)]">
          {t(lang, "premium_confirming")}
        </div>
      )}
    </div>
  );
}
